import React, { useState, useEffect } from 'react';
import { Typography, Button } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingOutlined, EyeOutlined } from '@ant-design/icons';
import { fetchCatalogoInfo, formatImageUrl } from '../../services/api';
import { capitalizeFirst } from '../../utils/stringUtils';
import './HeroBanner.css';
import CategorySelector from '../CategorySelector';

const { Title, Text } = Typography;

const HeroBanner = ({ onExploreClick, children }) => {
  const [current, setCurrent] = useState(0);
  const [catalogoInfo, setCatalogoInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cargar información del catálogo desde el backend
  useEffect(() => {
    const loadCatalogoInfo = async () => {
      try {
        const info = await fetchCatalogoInfo();
        setCatalogoInfo(info);
      } catch (error) {
        console.error('Error al cargar información del catálogo:', error);
        setCatalogoInfo({
          titulo_banner: "Channel's",
          subtitulo_banner: "Bisutería & Accesorios que Expresan tu Estilo Único",
          descripcion_banner: "Descubre nuestra colección exclusiva de bisutería y accesorios que complementan tu personalidad",
          boton_banner: "Explorar Colección",
          url_boton_banner: "#catalogo",
          banner_images: []
        });
      } finally {
        setLoading(false);
      }
    };

    loadCatalogoInfo();
  }, []);

  // Slides dinámicos
  const slides = React.useMemo(() => {
    if (!catalogoInfo) return [];
    
    let result = [];
    
    // 1. Agregar imágenes de la galería banner_images si existen
    if (catalogoInfo.banner_images && catalogoInfo.banner_images.length > 0) {
      result = catalogoInfo.banner_images.map((banner) => ({
        image: formatImageUrl(banner.url),
        title: banner.titulo || catalogoInfo.titulo_banner,
        subtitle: banner.subtitulo || catalogoInfo.subtitulo_banner,
        description: banner.descripcion || catalogoInfo.descripcion_banner,
        button: banner.boton || catalogoInfo.boton_banner,
        buttonUrl: banner.url_boton || catalogoInfo.url_boton_banner
      }));
    }
    
    // 2. Si no hay imágenes en la galería pero hay un banner_url principal, usarlo
    if (result.length === 0 && catalogoInfo.banner_url) {
      result.push({
        image: formatImageUrl(catalogoInfo.banner_url),
        title: catalogoInfo.titulo_banner,
        subtitle: catalogoInfo.subtitulo_banner,
        description: catalogoInfo.descripcion_banner,
        button: catalogoInfo.boton_banner,
        buttonUrl: catalogoInfo.url_boton_banner
      });
    }

    // 3. Si aún no hay nada, usar la imagen por defecto
    if (result.length === 0) {
      result.push({
        image: require('./banner.jpg'),
        title: catalogoInfo.titulo_banner || "Channel's",
        subtitle: catalogoInfo.subtitulo_banner || "Bisutería & Accesorios",
        description: catalogoInfo.descripcion_banner || "Descubre nuestra colección exclusiva",
        button: catalogoInfo.boton_banner || "Explorar Colección",
        buttonUrl: catalogoInfo.url_boton_banner || "#catalogo"
      });
    }
    
    return result;
  }, [catalogoInfo]);

  // Preloading de imágenes para evitar parpadeos
  useEffect(() => {
    if (slides.length > 0) {
      slides.forEach(slide => {
        const img = new Image();
        img.src = slide.image;
      });
    }
  }, [slides]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  // Content variants simplified para máximo rendimiento
  const contentVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    },
    exit: { 
      opacity: 0, 
      x: 20,
      transition: { duration: 0.4, ease: "easeIn" }
    }
  };

  const handleButtonClick = (buttonUrl) => {
    if (buttonUrl.startsWith('#')) {
      const element = document.querySelector(buttonUrl);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    } else if (buttonUrl.startsWith('http')) {
      window.open(buttonUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = buttonUrl;
    }
  };

  if (loading || slides.length === 0) {
    return (
      <div className="hero-banner-carousel loading">
        <div className="loader-container">
          <div className="elegant-loader"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-banner-carousel">
      {/* Capa de fondo fija con transición CSS para crossfade suave */}
      {slides.map((slide, index) => (
        <div
          key={`bg-${index}`}
          className={`hero-slide-bg ${current === index ? 'active' : ''}`}
          style={{ backgroundImage: `url(${slide.image})` }}
        />
      ))}
      
      <div className="banner-overlay" />
      
      <div className="banner-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={`content-${current}`}
            className="banner-text-wrapper"
            variants={contentVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <div className="banner-badge">Nueva Colección</div>
            <Title level={1} className="banner-title">
              {capitalizeFirst(slides[current].title)}
            </Title>
            
            <div className="banner-divider" />
            
            <Text className="banner-subtitle">
              {capitalizeFirst(slides[current].subtitle)}
            </Text>

            {slides[current].description && (
              <Text className="banner-description">
                {capitalizeFirst(slides[current].description)}
              </Text>
            )}
            
            <div className="banner-actions">
              <Button
                className="banner-button-elegant"
                icon={<EyeOutlined />}
                size="large"
                onClick={() => handleButtonClick(slides[current].buttonUrl)}
              >
                {slides[current].button}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      
      <div className="static-categories-container">
        <CategorySelector selected={null} onSelect={() => {}} loading={loading} />
      </div>
      
      {slides.length > 1 && (
        <div className="carousel-indicators-elegant">
          {slides.map((_, idx) => (
            <button
              key={idx}
              className={`indicator-dot ${current === idx ? 'active' : ''}`}
              onClick={() => setCurrent(idx)}
              aria-label={`Slide ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroBanner;
